/**
 * Downgrade markdown to a single line of readable plain text.
 *
 * Message bodies and task notes are stored as markdown, but several surfaces
 * show them as a bare one-line preview where markdown syntax is noise rather
 * than formatting: the push-notification body, the contact-list last-message
 * line, the doer home tile, and the reply-quote strip. Without this they read
 * as `**Shipped the deck** - see [the doc](https://…)`.
 *
 * Deliberately a stripper, not a parser — it has to run in the API too, so it
 * stays dependency-free. The one known inaccuracy: emphasis markers are also
 * stripped inside code spans, so `` `**x**` `` previews as `x`. That is fine
 * for a truncated preview and not worth a real parser.
 */
/** Characters markdown lets you escape with a backslash. */
const ESCAPABLE = '\\`*_{}[]()#+-.!>~|';
/** Private-use block the escaped characters hide in while the rules below run. */
const ESCAPE_BASE = 0xe000;
const ESCAPE_PLACEHOLDER_RE = new RegExp(`[\\u${ESCAPE_BASE.toString(16)}-\\u${(ESCAPE_BASE + ESCAPABLE.length - 1).toString(16)}]`, 'g');
export function markdownToPlainText(markdown) {
    if (!markdown)
        return '';
    let text = markdown;
    // Park escaped characters out of reach first. `a literal \*star\*` has to
    // survive the emphasis rules below: unescaping up front would just hand them
    // a real `*star*` to strip, and unescaping afterwards lets them strip it
    // directly. Neither leaves the literal the author asked for.
    text = text.replace(/\\(.)/gs, (match, char) => {
        const index = ESCAPABLE.indexOf(char);
        return index === -1 ? match : String.fromCharCode(ESCAPE_BASE + index);
    });
    // A backslash hard break is a line break and nothing else. Content written
    // since the shared editor landed never stores this form — `getMarkdown`
    // normalises it — but older rows and other markdown sources can.
    text = text.replace(/\\\n/g, '\n');
    // Fenced code blocks: keep the code, drop the fences and any info string.
    text = text.replace(/^[^\S\n]*(?:```|~~~)[^\n]*\n([\s\S]*?)^[^\S\n]*(?:```|~~~)[^\S\n]*$/gm, '$1');
    // Images before links — `![alt](src)` is a link pattern with a `!` in front,
    // so the link rule below would otherwise leave a stray `!`.
    text = text.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
    // Links, including the composer's mention form `[@Name](uuid)`, keep the label.
    text = text.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');
    // Autolinks: `<https://example.com>` -> the bare url.
    text = text.replace(/<((?:https?|mailto):[^>\s]+)>/g, '$1');
    // Line-leading block syntax: headings, blockquotes, list bullets and numbers.
    text = text.replace(/^[^\S\n]*#{1,6}[^\S\n]+/gm, '');
    text = text.replace(/^[^\S\n]*>[^\S\n]?/gm, '');
    text = text.replace(/^[^\S\n]*(?:[-*+]|\d{1,9}[.)])[^\S\n]+/gm, '');
    // Thematic breaks carry nothing once inline.
    text = text.replace(/^[^\S\n]*(?:-{3,}|\*{3,}|_{3,})[^\S\n]*$/gm, '');
    // Inline code.
    text = text.replace(/`+([^`]+)`+/g, '$1');
    // Emphasis. `**`/`__` first so the single-marker rules don't split them.
    text = text.replace(/\*\*(?=\S)([\s\S]*?\S)\*\*/g, '$1');
    text = text.replace(/(?<![\w_])__(?=\S)([\s\S]*?\S)__(?![\w_])/g, '$1');
    text = text.replace(/~~(?=\S)([\s\S]*?\S)~~/g, '$1');
    text = text.replace(/(?<![\w*])\*(?=\S)([^*\n]*?\S)\*(?![\w*])/g, '$1');
    // Intraword `_` is not emphasis in markdown, so snake_case and urls survive.
    text = text.replace(/(?<![\w_])_(?=\S)([^_\n]*?\S)_(?![\w_])/g, '$1');
    // Put the escaped characters back, now that nothing will read them as syntax.
    text = text.replace(ESCAPE_PLACEHOLDER_RE, (char) => ESCAPABLE[char.charCodeAt(0) - ESCAPE_BASE]);
    // Everything is one line by the time it reaches a preview.
    return text.replace(/\s+/g, ' ').trim();
}
//# sourceMappingURL=markdown-text.js.map